import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ChartLine, FileText, Upload, SignOut, House, Coins, Key, List, X, Megaphone, Warning } from '@phosphor-icons/react';
import { Button } from '../../components/ui/button';

/**
 * Responsive admin navigation.
 * Desktop: horizontal button row (wraps gracefully).
 * Mobile (<640px): hamburger menu dropdown — no horizontal scroll.
 */
const AdminNav = ({ title, titleTestId = 'admin-page-title' }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    navigate('/admin/login');
  };

  const links = [
    { to: '/', label: 'Home', icon: House, color: '#10B981', testid: 'nav-home' },
    { to: '/admin/dashboard', label: 'Dashboard', icon: ChartLine, color: '#334155', testid: 'nav-admin-dashboard' },
    { to: '/admin/rfqs', label: 'RFQs', icon: FileText, color: '#334155', testid: 'nav-admin-rfqs' },
    { to: '/admin/inventory', label: 'Inventory', icon: Upload, color: '#334155', testid: 'nav-admin-inventory' },
    { to: '/admin/web3-orders', label: 'Web3 Orders', icon: Coins, color: '#8B5CF6', testid: 'nav-admin-web3-orders' },
    { to: '/admin/disputes', label: 'Disputes', icon: Warning, color: '#EF4444', testid: 'nav-admin-disputes' },
    { to: '/admin/agent-keys', label: 'API Keys', icon: Key, color: '#10B981', testid: 'nav-admin-agent-keys' },
    { to: '/admin/directory-submissions', label: 'Directories', icon: Megaphone, color: '#F59E0B', testid: 'nav-admin-directories' },
  ];

  const go = (to) => {
    setMobileOpen(false);
    navigate(to);
  };

  return (
    <header className="backdrop-blur-xl bg-black/60 border-b border-white/10 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3">
        <div className="flex items-center justify-between gap-2">
          {/* Title */}
          <h1
            className="text-base sm:text-2xl font-['JetBrains_Mono'] font-bold truncate flex-1 min-w-0"
            data-testid={titleTestId}
          >
            {title}
          </h1>

          {/* Mobile: hamburger + logout */}
          <div className="flex items-center gap-1 sm:hidden">
            <Button
              onClick={() => setMobileOpen(o => !o)}
              variant="ghost"
              size="sm"
              className="text-white p-2"
              data-testid="admin-mobile-menu-btn"
              aria-label="Menu"
            >
              {mobileOpen ? <X size={22} /> : <List size={22} />}
            </Button>
            <Button
              onClick={handleLogout}
              variant="ghost"
              size="sm"
              className="text-[#EF4444] p-2"
              data-testid="logout-btn-mobile"
              aria-label="Logout"
            >
              <SignOut size={20} />
            </Button>
          </div>

          {/* Desktop: full horizontal nav */}
          <nav className="hidden sm:flex items-center gap-2 flex-wrap justify-end">
            {links.map(l => {
              const isActive = location.pathname === l.to;
              const Icon = l.icon;
              return (
                <Button
                  key={l.to}
                  onClick={() => go(l.to)}
                  variant="outline"
                  size="sm"
                  className="font-['JetBrains_Mono'] text-sm whitespace-nowrap"
                  style={{
                    borderColor: isActive ? l.color : '#334155',
                    color: isActive ? l.color : undefined,
                  }}
                  data-testid={l.testid}
                >
                  <Icon className="mr-1" size={16} />
                  {l.label}
                </Button>
              );
            })}
            <Button
              onClick={handleLogout}
              variant="ghost"
              size="sm"
              className="text-[#EF4444] hover:text-[#EF4444] font-['JetBrains_Mono']"
              data-testid="logout-btn"
            >
              <SignOut size={18} />
            </Button>
          </nav>
        </div>

        {/* Mobile dropdown */}
        {mobileOpen && (
          <div className="sm:hidden mt-3 pt-3 border-t border-white/10 flex flex-col gap-1" data-testid="admin-mobile-menu">
            {links.map(l => {
              const isActive = location.pathname === l.to;
              const Icon = l.icon;
              return (
                <button
                  key={l.to}
                  onClick={() => go(l.to)}
                  className="flex items-center gap-3 px-3 py-3 rounded-lg font-['JetBrains_Mono'] text-sm text-left hover:bg-white/5 transition"
                  style={{
                    color: isActive ? l.color : '#CBD5E1',
                    backgroundColor: isActive ? `${l.color}22` : 'transparent',
                  }}
                  data-testid={`mobile-${l.testid}`}
                >
                  <Icon size={18} style={{ color: l.color }} />
                  {l.label}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </header>
  );
};

export default AdminNav;
